import type { RuleArticle, DraftRuleArticle } from '@proref/types';
import type { CreateRuleArticleInput, UpdateRuleArticleInput } from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';

// Rule-article authoring (super_admin only — the API enforces the role). Reads live in
// sports.service (`listRules`) / useRulesQuery; this module is the write + AI-extract surface.

export async function createRuleArticle(
  slug: string,
  input: CreateRuleArticleInput,
): Promise<RuleArticle> {
  const res = await api.post<Envelope<RuleArticle>>(`/sports/${slug}/rules`, input);
  return unwrap(res.data);
}

export async function updateRuleArticle(
  id: string,
  input: UpdateRuleArticleInput,
): Promise<RuleArticle> {
  const res = await api.patch<Envelope<RuleArticle>>(`/rules/${id}`, input);
  return unwrap(res.data);
}

export async function deleteRuleArticle(id: string): Promise<void> {
  await api.delete<Envelope<null>>(`/rules/${id}`);
}

// The AI wizard's confirm step — create the reviewed set in one call.
export async function bulkCreateRuleArticles(
  slug: string,
  articles: CreateRuleArticleInput[],
): Promise<RuleArticle[]> {
  const res = await api.post<Envelope<RuleArticle[]>>(`/sports/${slug}/rules/bulk`, { articles });
  return unwrap(res.data);
}

// Send a base64 rule-book PDF to the AI extractor; returns draft articles (not persisted) for
// the admin to review before confirming.
export async function extractRulesFromPdf(
  slug: string,
  pdfBase64: string,
): Promise<DraftRuleArticle[]> {
  const res = await api.post<Envelope<{ articles: DraftRuleArticle[] }>>(
    `/sports/${slug}/rules/extract`,
    { pdf_base64: pdfBase64 },
  );
  return unwrap(res.data).articles;
}
